import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Shield, FileText, CheckCircle, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CGUAcceptanceModal({ isOpen, onAccept, onDecline, userEmail }) {
  const { getAuthHeaders } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAccept = async () => {
    if (!accepted) return;
    
    setLoading(true);
    try {
      // Record acceptance in backend
      const response = await fetch(`${API_URL}/api/auth/accept-terms`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          version: '1.0',
          accepted_at: new Date().toISOString()
        })
      });

      if (response.ok) {
        onAccept();
      } else {
        console.error('Failed to record acceptance');
        onAccept(); // Still allow to proceed
      }
    } catch (error) {
      console.error('Error recording acceptance:', error);
      onAccept(); // Still allow to proceed
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-zinc-900 border border-gold-500/30 rounded-2xl max-w-lg w-full overflow-hidden"
          >
            {/* Header */}
            <div className="p-6 border-b border-gold-500/20 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gold-500/10 mb-4">
                <FileText className="w-7 h-7 text-gold-500" />
              </div>
              <h2 className="text-xl font-heading font-bold text-white mb-2">
                Bienvenue sur Assistant by Léon
              </h2>
              <p className="text-zinc-400 text-sm">
                Avant de continuer, veuillez accepter nos conditions d'utilisation
              </p>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Summary */}
              <div className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-700">
                <p className="text-sm text-zinc-300 leading-relaxed">
                  Assistant by Léon fournit une analyse automatisée d'aide à la décision, à titre informatif. 
                  Le service ne remplace pas l'avis d'un professionnel qualifié et l'utilisateur reste 
                  seul responsable de ses décisions.
                </p>
              </div>

              {/* Link to full terms */}
              <a
                href="/conditions-generales"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-gold-500 hover:text-gold-400 transition-colors text-sm"
              >
                <ExternalLink className="w-4 h-4" />
                Lire les Conditions Générales d'Utilisation et de Vente complètes
              </a>

              {/* Checkbox */}
              <label className="flex items-start gap-3 p-4 bg-zinc-800/30 rounded-xl border border-zinc-700 cursor-pointer hover:bg-zinc-800/50 transition-colors">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-zinc-600 bg-zinc-800 text-gold-500 focus:ring-gold-500 focus:ring-offset-zinc-900"
                  data-testid="cgu-checkbox"
                />
                <span className="text-sm text-zinc-300 leading-relaxed">
                  J'accepte les{' '}
                  <a
                    href="/conditions-generales"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold-500 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Conditions Générales d'Utilisation et de Vente
                  </a>
                  {' '}d'Assistant by Léon
                </span>
              </label>
            </div>

            {/* Actions */}
            <div className="p-6 border-t border-gold-500/20 bg-zinc-900/50">
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={onDecline}
                  variant="outline"
                  className="flex-1 py-4 border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                  data-testid="cgu-decline-btn"
                >
                  Refuser
                </Button>
                <Button
                  onClick={handleAccept}
                  disabled={!accepted || loading}
                  className={`flex-1 py-4 font-bold transition-all ${
                    accepted && !loading
                      ? 'bg-gold-500 hover:bg-gold-400 text-black'
                      : 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                  }`}
                  data-testid="cgu-accept-btn"
                >
                  {loading ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Validation...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Accepter et continuer
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
